import type { ComponentPropsWithRef } from 'react';

import { cn } from '../../lib/cn';
import { Link } from '../display/Link';

export type FooterProps = Omit<ComponentPropsWithRef<'footer'>, 'children'>;

/**
 * The powered-by bar: one row, the copyright on the left, the vendor credit on
 * the right, and nothing else.
 *
 * It is deliberately the smallest thing that can carry those two facts. This bar
 * sits under every page in the product, and the block it replaced — the lockup,
 * the link columns, the social row, a divider — was a landing-page footer at
 * `py-10`, several times the height of the content it followed. The bar is
 * `h-9`, so it reads as the end of the page rather than as a second navigation.
 *
 * STICKY, WITHOUT `position: fixed`. The bar carries `mt-auto` and is the last
 * child of the shell's full-height flex column (`min-h-dvh flex flex-col`), so
 * the column pushes it to the bottom of the viewport on a short page and it sits
 * at the true end of the document on a long one.
 *
 * `position: fixed` would also keep it in view, and that is exactly why it is
 * not used here: a fixed bar is out of flow, so it covers the last row of a long
 * table and no amount of scrolling reveals what is underneath — the overlay
 * scroll-lock defect wearing a bar's clothes. `mt-auto` costs one scroll on a
 * long page and never hides a pixel.
 *
 * The shell change is load-bearing, not decoration: `mt-auto` does nothing
 * without a flex parent, and a flex column does nothing without `min-h-dvh` on
 * the shell.
 */
export function Footer({ className, ...rest }: FooterProps) {
  const year = new Date().getFullYear();

  return (
    <footer
      {...rest}
      className={cn('mt-auto w-full border-t border-line-subtle bg-surface', className)}
    >
      <div className="flex h-9 items-center justify-between gap-4 px-4 font-mono text-xs text-muted">
        {/* Collapsed below `sm` rather than wrapped. At 360px the copyright and
            the credit together are wider than the bar, and a wrap would take the
            bar past the 36px it is specified at — the credit is the one fact the
            bar exists for, so the copyright is the one that goes. */}
        <p className="hidden truncate sm:block">© {year} escrowbuild.io</p>

        <Link
          href="https://dhdtech.io"
          variant="muted"
          standalone
          target="_blank"
          rel="noopener noreferrer"
          aria-label="DHDTech.io (opens in a new tab)"
          className="ml-auto shrink-0"
        >
          {/* A raw `<img>`, so the `[&>svg]:size-full` discipline the other icon
              slots carry does not reach it — there is no `svg` child to size.
              The explicit `width`/`height` attributes are what stop the row
              reflowing while the file loads, and `size-4` is what holds the slot
              at 16px whatever text size the bar is eventually given. The source
              file has an opaque white ground, so it takes the house hue-neutral
              corner rather than sitting on the bar as a hard white square. */}
          <img
            src="/dhdtech-logo.png"
            alt=""
            width={16}
            height={16}
            className="size-4 shrink-0 rounded-xs object-contain"
          />
          DHDTech.io
        </Link>
      </div>
    </footer>
  );
}
